import { Action, CaseReducer, Draft, EntityState } from '@reduxjs/toolkit';
import { Area } from '@ff-os/shared-utils/types/Area';
import { FulfilledUploadAction, PendingUploadAction, RejectedUploadAction } from '@ff-os/shared-utils/types/private-assets/uploadAction';
import { unwrap } from '@ff-os/shared-utils/unwrap';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { PhotoPrintsUploadContext } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsUploadContext';
import { addOne } from '../photosSlice';

type PhotoPrintCaseReducer<T extends Action> = CaseReducer<EntityState<PhotoPrintsJson.Photo>, T>;

// Helper function to calculate crop with forced aspect ratio for uploaded photos
const calculateInitialCropWithForcedAspectRatio = (
    imageWidth: number,
    imageHeight: number,
    size?: PhotoPrintsJson.Size,
    hasBorder?: boolean
): Area => {
    // Default crop covers the full image
    let crop: Area = {
        x: 0,
        y: 0,
        width: imageWidth,
        height: imageHeight
    };

    // If the size has a forced aspect ratio and borders are applied, apply it
    if (size && size.borderAspectRatio && hasBorder) {
        const [widthStr, heightStr] = size.borderAspectRatio.split('/');
        const overrideWidth = parseFloat(widthStr);
        const overrideHeight = parseFloat(heightStr);

        if (!isNaN(overrideWidth) && !isNaN(overrideHeight) && overrideHeight !== 0) {
            // Use the exact aspect ratio from border-aspect-ratio
            const targetAspectRatio = overrideWidth / overrideHeight;

            // Apply a crop factor only for retro sizes to leave space on all sides
            // const cropFactor = size.isRetro ? 0.85 : 1.0;

            // Calculate available space with crop factor applied
            const availableWidth = imageWidth * 1;
            const availableHeight = imageHeight * 1;

            // Calculate new crop dimensions that fit the specified aspect ratio within the available space
            let newCropWidth, newCropHeight;

            // Try to fit by the dimension that gives us the largest crop within available space
            const widthBasedHeight = availableWidth / targetAspectRatio;
            const heightBasedWidth = availableHeight * targetAspectRatio;

            if (widthBasedHeight <= availableHeight) {
                // Fit by width
                newCropWidth = availableWidth;
                newCropHeight = widthBasedHeight;
            } else {
                // Fit by height
                newCropWidth = heightBasedWidth;
                newCropHeight = availableHeight;
            }

            // Center the crop within the image (this automatically leaves space on all sides)
            const borderX = (imageWidth - newCropWidth) / 2;
            const borderY = (imageHeight - newCropHeight) / 2;

            crop = {
                x: borderX,
                y: borderY,
                width: newCropWidth,
                height: newCropHeight
            };
        }
    }

    return crop;
};

export const handlePending: PhotoPrintCaseReducer<PendingUploadAction<PhotoPrintsUploadContext>> = ((
    state, action
) => {
    const requestContext = action.meta.arg.context;

    const photo: PhotoPrintsJson.UnprocessedPhoto = {
        id: requestContext.id,
        groupId: requestContext.groupId,
        assetId: requestContext.assetId,
        quantity: 1,
        processed: false,
        assetSource: 'local',
        status: 'saving',
        filter: null,
        fileName: requestContext.fileName
    };

    if (requestContext.processed) {
        const existingPhoto = state.entities[requestContext.id];
        const existingSeed = (existingPhoto as PhotoPrintsJson.ProcessedPhoto)?.magicBorderRngSeed;

        // Calculate initial crop with forced aspect ratio if applicable
        const initialCrop = calculateInitialCropWithForcedAspectRatio(
            requestContext.imageWidth!,
            requestContext.imageHeight!,
            requestContext.sizeConfig,
            requestContext.hasBorder
        );

        addOne(state, {
            ...photo,
            processed: requestContext.processed,
            localUrl: requestContext.localUrl!,
            width: requestContext.imageWidth!,
            height: requestContext.imageHeight!,
            capturedOn: requestContext.capturedOn,
            crop: initialCrop,
            autoFaceCenter: requestContext.autoFaceCenter ?? true,
            detections: requestContext.detections,
            magicBorderRngSeed: existingSeed ?? `${requestContext.id}-${Date.now()}`
        });
    } else {
        addOne(state, photo);
    }
});

export const handleRejected: PhotoPrintCaseReducer<RejectedUploadAction<PhotoPrintsUploadContext>> = ((
    state, action
) => {

    const requestMeta = action.meta.arg.context;
    const idsToUpdate = unwrap(state)
        .filter(p => p.assetId === requestMeta.assetId)
        .map(p => p.id);

    for (const idToUpdate of idsToUpdate) {
        const photo = state.entities[idToUpdate]!;
        photo.status = 'error';
    }
});

export const handleFulfilled: PhotoPrintCaseReducer<FulfilledUploadAction<PhotoPrintsUploadContext>> = (
    state, action
) => {

    const requestMeta = action.meta.arg.context;
    const idsToUpdate = unwrap(state)
        .filter(p => p.assetId === requestMeta.assetId)
        .map(p => p.id);

    for (const idToUpdate of idsToUpdate) {

        const photo = state.entities[idToUpdate]! as Draft<PhotoPrintsJson.ProcessedPhoto>;

        photo.processed = true;
        photo.status = 'saved';
        photo.assetId = action.payload.assetId;
        photo.assetSource = 'private';

        const { width, height } = action.payload.meta;

        if (!requestMeta.localUrl) {

            photo.width = width;
            photo.height = height;
            photo.crop = calculateInitialCropWithForcedAspectRatio(
                width,
                height,
                requestMeta.sizeConfig,
                requestMeta.hasBorder
            );

        } else if (width === photo.height && height === photo.width) {

            // Fix images with xmp tag orientations 5 - 8 #17453

            photo.width = width;
            photo.height = height;
            photo.crop = calculateInitialCropWithForcedAspectRatio(
                width,
                height,
                requestMeta.sizeConfig,
                requestMeta.hasBorder
            );
        }
    }
};